/**
 * Polyglot MP3/ZIP technique.
 *
 * Encoding: Appends a ZIP archive (containing "payload.bin") after the MP3 data.
 * Decoding: Finds the ZIP signature (PK\x03\x04) in the file, extracts payload.bin.
 *
 * Both encode and decode work 100% client-side.
 */

import JSZip from "jszip";

const ZIP_SIGNATURE = new Uint8Array([0x50, 0x4b, 0x03, 0x04]); // "PK\x03\x04"
const PAYLOAD_FILENAME = "payload.bin";

/**
 * Create a polyglot MP3/ZIP by appending a ZIP archive to the MP3 data.
 * The ZIP contains "payload.bin" with the full stego blob (salt+header+ciphertext).
 */
export async function createPolyglot(
  mp3Data: Uint8Array,
  blob: Uint8Array
): Promise<Uint8Array> {
  // Find MP3 end (check for ID3v1 tag at the end)
  let mp3End = mp3Data.length;
  if (mp3Data.length >= 128) {
    const tagOffset = mp3Data.length - 128;
    if (
      mp3Data[tagOffset] === 0x54 && // 'T'
      mp3Data[tagOffset + 1] === 0x41 && // 'A'
      mp3Data[tagOffset + 2] === 0x47 // 'G'
    ) {
      mp3End = tagOffset;
    }
  }

  // Create ZIP in memory
  const zip = new JSZip();
  zip.file(PAYLOAD_FILENAME, blob);
  const zipData = await zip.generateAsync({
    type: "uint8array",
    compression: "DEFLATE",
    compressionOptions: { level: 9 },
  });

  // Concatenate: MP3 (without ID3v1) + ZIP
  const result = new Uint8Array(mp3End + zipData.length);
  result.set(mp3Data.subarray(0, mp3End));
  result.set(zipData, mp3End);
  return result;
}

/**
 * Extract payload.bin from a polyglot MP3/ZIP file.
 * Scans for ZIP signature starting at offset 1024 (skip ID3v2 tags).
 * Returns null if no valid ZIP found.
 */
export async function extractPolyglot(
  data: Uint8Array
): Promise<Uint8Array | null> {
  const zipStart = findZipSignature(data);
  if (zipStart < 0) return null;

  const zipData = data.slice(zipStart);
  const zip = await JSZip.loadAsync(zipData);
  const file = zip.file(PAYLOAD_FILENAME);
  if (!file) return null;

  return new Uint8Array(await file.async("uint8array"));
}

/**
 * Find the first valid ZIP local file header after offset 1024.
 * Validates filename length and extra field length to avoid false positives.
 */
function findZipSignature(data: Uint8Array): number {
  const startOffset = 1024; // Skip ID3v2 tags
  for (let i = startOffset; i <= data.length - 4; i++) {
    if (
      data[i] === ZIP_SIGNATURE[0] &&
      data[i + 1] === ZIP_SIGNATURE[1] &&
      data[i + 2] === ZIP_SIGNATURE[2] &&
      data[i + 3] === ZIP_SIGNATURE[3]
    ) {
      // Validate: filename length at offset+26 (2 bytes LE), extra at offset+28
      if (i + 30 <= data.length) {
        const filenameLen = data[i + 26]! | (data[i + 27]! << 8);
        const extraLen = data[i + 28]! | (data[i + 29]! << 8);
        if (filenameLen > 0 && filenameLen < 1024 && extraLen < 65535) {
          return i;
        }
      }
    }
  }
  return -1;
}
